import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Mail, ShieldCheck, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { employeeInvitations } from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * AcceptInvite
 * ────────────
 * Public landing for `/accept-invite/:token` — the link the new hire
 * receives by email when an admin sends them an employee invitation
 * (roadmap item #17 Phase 1).
 *
 * Flow:
 *   1. On mount, look up the token. The backend returns the invitee's
 *      email + role + who invited them, or a friendly error code if the
 *      token is invalid / expired / already accepted / revoked.
 *   2. Show a "Welcome to {business}" panel with a name + password form.
 *   3. On submit, POST /accept. The response is shaped exactly like
 *      /auth/login, so we hand it straight to AuthContext.applySession
 *      and redirect to /.
 *
 * Deliberately renders without any layout chrome that requires a logged-in
 * user — the page is reachable from a fresh browser with no session.
 */

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await employeeInvitations.lookup(token);
        if (cancelled) return;
        setInvite(res.data);
      } catch (err) {
        if (cancelled) return;
        // The backend maps each invalid case to a distinct status + message;
        // we just surface it.
        const status = err.response?.status;
        const message = err.response?.data?.error
          || (status === 404 ? 'This invitation link is not valid.'
              : status === 410 ? 'This invitation is no longer usable.'
              : 'Could not load this invitation.');
        setError({ status, message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!form.firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await employeeInvitations.accept(token, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim() || null,
        password: form.password,
      });
      // Same shape as /auth/login — drop straight into the session.
      applySession(res.data);
      toast.success(`Welcome aboard, ${form.firstName.trim()}!`);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not accept invitation');
      // If the token went bad mid-flow (race with admin revoking, etc),
      // re-render the error state instead of leaving the form mounted.
      if (err.response?.status === 410 || err.response?.status === 404) {
        setError({ status: err.response.status, message: err.response.data?.error || 'Invitation no longer valid.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-md mx-auto px-4">
          <div className="card border-red-200 bg-red-50 text-center">
            <AlertCircle className="mx-auto text-red-600 mb-3" size={48} />
            <h1 className="text-xl font-bold text-red-900 mb-2">Invitation unavailable</h1>
            <p className="text-red-800 mb-4">{error.message}</p>
            <p className="text-sm text-red-700">
              If you believe this is a mistake, contact the person who sent the invitation and ask them to resend it.
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="btn btn-secondary mt-6"
            >
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const businessName = invite?.business_name || 'the team';
  const inviterName = invite?.invited_by_name || 'an administrator';
  const roleLabel = invite?.role === 'admin' ? 'Administrator' : 'Team member';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-md mx-auto px-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-700 rounded-full mb-4">
            <ShieldCheck size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome to {businessName}!</h1>
          <p className="text-gray-600 mt-2">
            <span className="font-medium">{inviterName}</span> invited you to join as {roleLabel.toLowerCase()}.
          </p>
        </div>

        <div className="card">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 bg-gray-50 rounded p-3">
            <Mail size={16} className="text-gray-500" />
            <span>You'll sign in with</span>
            <span className="font-medium text-gray-900">{invite.email}</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="input w-full"
                  required
                  autoFocus
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="input w-full"
                  maxLength={100}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input w-full"
                maxLength={30}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input w-full pr-10"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 6 characters.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm password <span className="text-red-500">*</span>
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                className="input w-full"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={submitting}
            >
              {submitting ? 'Setting up your account…' : 'Accept invitation & sign in'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500 mt-4">
          Already have an account? <a href="/login" className="text-primary-600 hover:text-primary-800">Sign in instead</a>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
